import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import reactHtmlParser from 'react-html-parser';
import { Branch } from '../../api/articles/Branch';
import Page from '../../components/Page';
import PageHeadline from '../../components/PageHeadline';

export interface BranchDetailPageLayoutProps extends StandardDivProps<BranchDetailPageLayoutClassKey> {
  branchDetail: Branch;
}

export type BranchDetailPageLayoutClassKey = 'root' | 'content' | 'image' | 'hours' | 'headlineContainer';

const themeSettings = { name: 'BranchDetailPageLayout' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<BranchDetailPageLayoutClassKey, {}>({
      root: {},
      content: {
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        overflow: 'hidden',
        '& h4': {
          marginBottom: theme.spacing(0),
        },
      },
      image: {
        width: '100%',
        maxWidth: 500,
        [theme.breakpoints.down('sm')]: {
          maxWidth: '100%',
        },
      },
      hours: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: theme.spacing(0, 2),
        '& h4': {
          margin: theme.spacing(1, 0),
        },
        '& a': {
          textDecoration: 'none',
          color: theme.palette.text.primary,
          transition: 'all .2s ease',
          '&:hover': {
            textDecoration: 'underline',
          },
        },
        [theme.breakpoints.down('sm')]: {
          padding: theme.spacing(0.5, 0),
        },
      },
      headlineContainer: {
        display: 'flex',
        alignItems: 'flex-end',
        [theme.breakpoints.down('sm')]: {
          flexDirection: 'column',
          alignItems: 'flex-start',
        },
      },
    }),
  themeSettings,
);

const BranchDetailPageLayout = React.forwardRef<HTMLDivElement, BranchDetailPageLayoutProps>(
  (
    {
      branchDetail: {
        contactHeadOfBranch,
        contactPhone,
        contactEmail,
        contactPostAddress,
        openingHours,
        title: pageTitle,
        image,
        body,
      },
      className,
      classes: pClasses,
    },
    ref,
  ) => {
    const classes = useStyles({ classes: pClasses });

    return (
      <Page ref={ref} className={clsx(classes.root, className)} pageTitle={pageTitle}>
        <PageHeadline>{pageTitle}</PageHeadline>
        <div className={classes.headlineContainer}>
          <img src={image} alt={pageTitle} className={classes.image} />
          <div className={classes.hours}>
            {contactHeadOfBranch && (
              <>
                <h4>Kontaktní osoba:</h4> {contactHeadOfBranch}
              </>
            )}
            <h4>Telefon:</h4> <a href={`tel:${contactPhone}`}>{contactPhone}</a>
            <h4>Email:</h4> <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
            <h4>Adresa:</h4> {contactPostAddress}
            {openingHours && (
              <>
                <h4>Otevírací doba:</h4> {reactHtmlParser(openingHours)}
              </>
            )}
          </div>
        </div>
        <div className={classes.content}>
          <h4>Nabídka prodejny:</h4>
          {reactHtmlParser(body)}
        </div>
      </Page>
    );
  },
);

BranchDetailPageLayout.displayName = 'BranchDetailPageLayout';

export default withThemeProps(themeSettings)(BranchDetailPageLayout);
